import api from '@/api/surveillances'
import user from '@/store/user'
import surveillance from '@/sui/data/surveillance'
import ice from '@/sui/tools/ice'

const state = {
  all: {
    '2019': [],
    '2020': [],
    '2021': [],
    '2022': [],
    '2023': [],
    '2024': [],
    '2025': [],
    '2026': []
  },

  state: 'empty'
}

function callThenLoadAll (f) {
  return ({ commit, dispatch }, request) => {
    return api[f](request).then(
      (r) => {
        if (r.data.err == null) {
          dispatch('loadAll')
        }
        return r
      }
    )
  }
}

// getters
const getters = {
  all: (state) => state.all,
  get: (state, getters) => (year, id) => {
    const result = getters.all[year].find(obj => obj.id === id)
    return result
  },
  loaded: (state) => state.state === 'loaded',
  error: (state) => state.state === 'error'
}

// actions
const actions = {
  loadAll ({ commit, state }) {
    commit('reset')

    const promises = [
      api.all2019(),
      api.all()
    ]

    return Promise.all(promises).then((values) => {
      const surveillances = []
      for (let i in values) {
        const { data } = values[i]
        if (data.err != null || data.v == null) {
          commit('clearSurveillances')
          return { err: data.err }
        } else {
          surveillances.push(data.v)
        }
      }
      commit('setSurveillances', surveillances)
      return values
    }).catch(e => {
      // console.log(e)
      commit('clearSurveillances')
      return { err: e }
    })
  },

  subscribe ({ commit, dispatch }, data) {
    return api.subscribe(data).then((response) => {
      if (response.data.err == null) {
        dispatch('loadAll')
      }
      return response
    })
  },

  edit ({ commit, dispatch }, request) {
    const r = surveillance.updateRequest(request)
    return api.edit(r).then((response) => {
      if (response.data.err == null) {
        dispatch('loadAll')
      }
      return response
    })
  },

  close ({ commit }, data) {
    return api.close(data).then(
      (response) => {
        if (response.data.err == null) {
          commit('setStatus', { surveillance: data.i, status: 'closed' })
        }
        return response
      }
    )
  },

  updateConventionMembers ({ dispatch }, request) {
    return api.updateConventionMembers(request).then((response) => {
      if (response.data.err == null) {
        dispatch('loadAll')
      }
      return response
    })
  },

  uploadConvention ({ dispatch }, request) {
    return api.uploadConvention(request.progress, request.data).then(
      (response) => {
        if (response.data.err == null) {
          dispatch('loadAll')
        }
        return response
      }
    )
  },

  confirmConvention ({ dispatch }, request) {
    return api.confirmConvention(request).then(
      r => {
        if (r.data.err == null) {
          dispatch('loadAll')
        }
        return r
      }
    )
  },

  addMember: callThenLoadAll('addMember'),
  updateMember: callThenLoadAll('updateMember'),
  removeMember: callThenLoadAll('removeMember'),
  addMember2019: callThenLoadAll('addMember2019'),
  updateMember2019: callThenLoadAll('updateMember2019'),
  removeMember2019: callThenLoadAll('removeMember2019'),
  removeDataset: callThenLoadAll('removeDataset'),
  updateDataset: callThenLoadAll('updateDataset'),
  updateDatasetSheets: callThenLoadAll('updateDatasetSheets'),
  updateDatasetDenominators: callThenLoadAll('updateDatasetDenominators'),
  getDatasetSheets (_, request) {
    return api.getDatasetSheets(request)
  },

  getDatasetDenominators (_, request) {
    return api.getDatasetDenominators(request)
  },

  getCompleteDataset (_, request) {
    return api.getCompleteDataset(request)
  },

  newExplaurSwab: callThenLoadAll('newExplaurSwab'),

  setExplaurPatient: callThenLoadAll('setExplaurPatient'),

  setExplaurSwabStatus: callThenLoadAll('setExplaurSwabStatus'),

  startExplaur: callThenLoadAll('startExplaur'),

  updateReaExplaurStatus: callThenLoadAll('updateReaExplaurStatus'),

  setSwab ({ dispatch }, request) {
    return api.setSwab(request).then((response) => {
      if (response.data.err == null) {
        dispatch('loadAll')
      }
      return response
    })
  },

  updateReaSinkStatus ({ dispatch }, request) {
    return api.updateReaSinkStatus(request).then((response) => {
      if (response.data.err == null) {
        dispatch('loadAll')
      }
      return response
    })
  },

  setEmptyBact ({ commit }, request) {
    return api.setEmptyBact(request).then(
      (response) => {
        if (response.data.err == null) {
          commit('setEmptyBact', request)
        }
        return response
      }
    )
  }
}

// mutations
const mutations = {
  reset (state) {
    state.state = 'loading'
  },

  setSingleSheetStatus (state, request) {
    const y = `${request.year}`
    const surveillance = state.all[y].find(s => s.id === request.id)
    if (surveillance == null) {
      return
    }
    surveillance[request.type].shst = request.st
  },

  clearSurveillances (state) {
    state.all = {
      '2019': [],
      '2020': [],
      '2021': [],
      '2022': [],
      '2023': [],
      '2024': [],
      '2025': [],
      '2026': []
    }
    state.state = 'error'
  },

  setSurveillances (state, data) {
    state.state = 'loaded'

    const result = {
      '2019': [],
      '2020': [],
      '2021': [],
      '2022': [],
      '2023': [],
      '2024': [],
      '2025': [],
      '2026': []
    }
    data.forEach(
      year => {
        year.forEach(
          ss => {
            let s = null
            if (ss.year === 2019) {
              // ignore 2019 surveillance for now, as they are in both lists
              return
            }

            if (ss.year == null) {
              s = surveillance.serverToClient2019(ss)
            } else {
              s = surveillance.serverToClient(ss)
            }
            s = surveillance.addMethods(s)
            s.fillAvalaibleModules(user.state.info.id)
            result[`${s.year}`].push(s)
          }
        )
      }
    )
    state.all = ice.freeze(result)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
